// Phase H.13b — Clause picker modal.
//
// Used by:
//  1. Body editor toolbar "Insert clause" — drops the chosen clause's
//     body_html at the cursor as a one-time copy (not a live reference).
//  2. Footnote / endnote popover "Insert from library" — filtered to
//     clauses whose category is exactly "Footnote" or "Citation"
//     (case-insensitive). On insert, strips HTML to plain text so the
//     footnote textarea stays a single editable string.
//
// Search filters name/description/category client-side over the fetched
// list — keeps the modal snappy and avoids a round-trip per keystroke.

import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Loader2, Search, BookOpen, ArrowRight } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const FOOTNOTE_CATEGORIES = ["Footnote", "Citation"];

function _stripHtml(html) {
    if (!html) return "";
    const tmp = document.createElement("div");
    tmp.innerHTML = html;
    // Replace common block-ending tags with a space first so adjacent
    // sentences don't collide once tags are removed.
    return (tmp.textContent || tmp.innerText || "")
        .replace(/\s+\n/g, "\n")
        .replace(/\n+/g, " ")
        .replace(/\s{2,}/g, " ")
        .trim();
}

export default function ClausePickerModal({
    open,
    onOpenChange,
    mode = "body",              // "body" | "footnote" — drives filtering + insert behaviour
    onInsert,                   // (clause) => void — receives the full clause object
}) {
    const [clauses, setClauses] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [selectedId, setSelectedId] = useState(null);

    // Fetch on open. For the footnote mode we fire 1 request per category
    // and merge — keeps the backend filter exact-case-insensitive without
    // adding a multi-value `category` query param.
    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        (async () => {
            setLoading(true);
            setSelectedId(null);
            try {
                if (mode === "footnote") {
                    const results = await Promise.all(
                        FOOTNOTE_CATEGORIES.map((c) =>
                            api.get(`/admin/clauses?category=${encodeURIComponent(c)}`)
                        )
                    );
                    if (cancelled) return;
                    const merged = results.flatMap((r) => r.data?.items || []);
                    setClauses(merged);
                } else {
                    const r = await api.get("/admin/clauses");
                    if (cancelled) return;
                    setClauses(r.data?.items || []);
                }
            } catch (err) {
                if (!cancelled) toast.error(formatApiError(err));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [open, mode]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return clauses;
        return clauses.filter((c) => {
            const hay = [c.name, c.description, c.category].filter(Boolean).join(" ").toLowerCase();
            return hay.includes(q);
        });
    }, [clauses, search]);

    // Group by category, alphabetical within group, categories sorted.
    const grouped = useMemo(() => {
        const map = new Map();
        for (const c of filtered) {
            const cat = c.category || "Uncategorized";
            if (!map.has(cat)) map.set(cat, []);
            map.get(cat).push(c);
        }
        for (const arr of map.values()) arr.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
    }, [filtered]);

    const selected = useMemo(
        () => clauses.find((c) => c.id === selectedId) || null,
        [clauses, selectedId]
    );

    const isEmptyOverall = !loading && clauses.length === 0;

    const handleInsert = () => {
        if (!selected || !onInsert) return;
        // Footnote mode: caller wants plain text. Body mode: caller wants HTML.
        onInsert({
            ...selected,
            _plain_text: _stripHtml(selected.body_html),
        });
        onOpenChange(false);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-3xl p-0 overflow-hidden"
                data-testid={`clause-picker-modal-${mode}`}
            >
                <DialogHeader className="px-5 py-3 border-b border-slate-200 bg-slate-50">
                    <DialogTitle className="flex items-center gap-2 text-base">
                        <BookOpen size={16} />
                        {mode === "footnote" ? "Insert footnote from library" : "Insert clause from library"}
                    </DialogTitle>
                    <div className="relative mt-2">
                        <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                        <Input
                            placeholder="Search name, category, or description…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            data-testid="clause-picker-search"
                            className="pl-7 h-8 text-sm"
                            autoFocus
                        />
                    </div>
                </DialogHeader>

                <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] min-h-[360px] max-h-[60vh]">
                    {/* Grouped list */}
                    <ul className="overflow-y-auto border-r border-slate-200 bg-white"
                        data-testid="clause-picker-list">
                        {loading && (
                            <li className="p-4 text-sm text-slate-500 inline-flex items-center gap-2">
                                <Loader2 className="animate-spin" size={14} /> Loading clauses…
                            </li>
                        )}
                        {!loading && isEmptyOverall && (
                            <li className="p-4 text-xs text-slate-600 leading-relaxed"
                                data-testid="clause-picker-empty">
                                {mode === "footnote" ? (
                                    <>
                                        No footnote clauses in library yet. Add one with category
                                        {" "}<code className="bg-slate-100 px-1 rounded">Footnote</code> or
                                        {" "}<code className="bg-slate-100 px-1 rounded">Citation</code> in
                                        {" "}<a href="/admin/clauses" className="underline text-blue-700">/admin/clauses</a>.
                                    </>
                                ) : (
                                    <>
                                        No clauses in library yet. Add one in
                                        {" "}<a href="/admin/clauses" className="underline text-blue-700">/admin/clauses</a>.
                                    </>
                                )}
                            </li>
                        )}
                        {!loading && !isEmptyOverall && filtered.length === 0 && (
                            <li className="p-4 text-xs text-slate-500">No matches for “{search}”.</li>
                        )}
                        {grouped.map(([cat, arr]) => (
                            <li key={cat}>
                                <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider font-medium text-slate-500 bg-slate-100/70 border-b border-slate-200">
                                    {cat}
                                </div>
                                <ul>
                                    {arr.map((c) => (
                                        <li key={c.id}>
                                            <button
                                                type="button"
                                                data-testid={`clause-row-${c.id}`}
                                                onClick={() => setSelectedId(c.id)}
                                                onDoubleClick={() => { setSelectedId(c.id); setTimeout(handleInsert, 50); }}
                                                className={`w-full text-left px-3 py-2 border-b border-slate-100 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${selectedId === c.id ? "bg-blue-50 border-l-2 border-l-blue-700" : ""}`}
                                            >
                                                <div className="text-sm font-medium text-slate-900 truncate">{c.name}</div>
                                                {c.description && (
                                                    <div className="text-[11px] text-slate-500 truncate">{c.description}</div>
                                                )}
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </li>
                        ))}
                    </ul>

                    {/* Preview */}
                    <section className="overflow-y-auto p-4 bg-slate-50">
                        {!selected && (
                            <div className="h-full flex items-center justify-center text-sm text-slate-400">
                                Select a clause on the left to preview it.
                            </div>
                        )}
                        {selected && (
                            <div>
                                <h3 className="text-base font-semibold text-slate-900 mb-1">{selected.name}</h3>
                                <p className="text-[11px] uppercase tracking-wider text-slate-500 mb-3">
                                    {selected.category || "Uncategorized"}
                                </p>
                                <div
                                    data-testid="clause-picker-preview"
                                    className="prose prose-sm max-w-none p-3 bg-white border border-slate-200 rounded-md max-h-[40vh] overflow-auto"
                                    dangerouslySetInnerHTML={{ __html: selected.body_html || "<p class='text-slate-400'>Empty body</p>" }}
                                />
                            </div>
                        )}
                    </section>
                </div>

                <DialogFooter className="px-5 py-3 border-t border-slate-200 bg-white">
                    <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        data-testid="clause-picker-cancel"
                        onClick={() => onOpenChange(false)}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        size="sm"
                        className="bg-blue-900 hover:bg-blue-800"
                        data-testid="clause-picker-insert"
                        disabled={!selected}
                        onClick={handleInsert}
                    >
                        Insert <ArrowRight size={12} className="ml-1" />
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
